import { ORDER_CURRENCIES, type OrderCurrency } from './currencies';
import type { SupplierOrderDto, SupplierOrderItemDto } from '../../dto/supplierOrder';
import type { SupplierOrderItemPayload, SupplierOrderPayload } from './api/supplierOrdersApi';

/**
 * Supplier currency defaults for a ZD (usability round 2026-10-05). One home
 * for the rule used by the ZD card („Wybierz dostawcę” of a saved draft and of
 * a new ZD, which the choice saves): after the supplier is chosen, the ZD summary currency and every
 * line nobody priced yet take the supplier's default currency. A line that
 * already has its own price or its own currency is the operator's choice and
 * stays as it is.
 */

export const resolveSupplierDefaultCurrency = (
  supplier?: { defaultCurrency?: string | null } | null,
): OrderCurrency | null => {
  const currency = supplier?.defaultCurrency?.trim().toUpperCase();
  return currency && (ORDER_CURRENCIES as readonly string[]).includes(currency)
    ? currency as OrderCurrency
    : null;
};

/**
 * Review PR #273: the one guard of the supplier choice („Wybierz dostawcę”
 * window of the ZD card). The supplier's currency is applied only for a purchase-price
 * editor, only while nobody picked a currency by hand, and only when it
 * differs from the current one.
 */
export const shouldApplySupplierCurrency = ({
  canEditPurchasePrices,
  currencyTouched,
  supplierCurrency,
  currentCurrency,
}: {
  canEditPurchasePrices: boolean;
  currencyTouched: boolean;
  supplierCurrency: string | null;
  currentCurrency: string | null | undefined;
}): boolean => Boolean(
  canEditPurchasePrices
  && !currencyTouched
  && supplierCurrency
  && supplierCurrency !== (currentCurrency || 'PLN'),
);

/** True when a line still follows the ZD currency: no own currency and no price yet. */
export const lineFollowsOrderCurrency = (
  line: { currency?: string | null; unitCost?: number | string | null },
  orderCurrency: string,
): boolean => (
  (!line.currency || line.currency === orderCurrency) && !(Number(line.unitCost || 0) > 0)
);

export function retargetLineCurrencies<T extends { currency?: string | null; unitCost?: number | string | null }>(
  lines: T[],
  fromCurrency: string,
  toCurrency: string,
): T[] {
  return lines.map((line) => (
    lineFollowsOrderCurrency(line, fromCurrency) ? { ...line, currency: toCurrency } : line
  ));
}

/** A saved ZD line as the PUT items payload (unchanged quantities and price). */
export const supplierOrderItemToPayload = (
  item: SupplierOrderItemDto,
  fallbackCurrency: string,
): SupplierOrderItemPayload => ({
  id: item.id || null,
  productVariantId: item.productVariantId || null,
  name: item.name,
  quantity: item.quantity,
  quantityPcs: item.quantityPcs ?? null,
  quantityM2: item.quantityM2 ?? null,
  quantityM3: item.quantityM3 ?? null,
  unit: (item.unit || 'pcs') as SupplierOrderItemPayload['unit'],
  purchaseQuantity: item.purchaseQuantity ?? item.quantity,
  purchaseUnit: (item.purchaseUnit || item.unit || 'pcs') as SupplierOrderItemPayload['unit'],
  conversionQuantity: item.conversionQuantity,
  conversionUnit: (item.conversionUnit ?? null) as SupplierOrderItemPayload['conversionUnit'],
  width: item.width ?? null,
  height: item.height ?? null,
  length: item.length ?? null,
  thickness: item.thickness ?? null,
  currency: item.currency || fallbackCurrency,
  unitCost: Number(item.unitCost ?? 0),
  description: item.description ?? null,
});

/**
 * PUT payload of „Wybierz dostawcę” on the ZD card. `currency` is sent only by
 * a user who edits purchase prices; then the summary currency changes and the
 * lines that still follow it (unpriced, no own currency) move with it.
 */
export function buildChooseSupplierUpdateData(
  order: Pick<SupplierOrderDto, 'currency' | 'items'>,
  supplierId: string,
  currency?: string | null,
): SupplierOrderPayload {
  const orderCurrency = order.currency || 'PLN';
  const data: SupplierOrderPayload = { supplierId };
  if (!currency || currency === orderCurrency) return data;
  data.currency = currency;
  data.items = retargetLineCurrencies(
    (order.items || []).map((item) => supplierOrderItemToPayload(item, orderCurrency)),
    orderCurrency,
    currency,
  );
  return data;
}

/**
 * The ZD value as the list shows it: in the ZD currency, or — when every line
 * is in one other currency (e.g. a draft whose summary stayed PLN while the
 * lines are in EUR) — in that line currency, so the list says „€600,00” like
 * any other EUR ZD. The PLN value is always the saved `totalAmountPLN`.
 */
export function supplierOrderDocumentAmount(
  order: Pick<SupplierOrderDto, 'currency' | 'totalAmount' | 'items'>,
): { amount: number; currency: string } {
  const orderCurrency = order.currency || 'PLN';
  const items = order.items || [];
  const lineCurrencies = new Set(items.map((item) => item.currency || orderCurrency));
  if (items.length > 0 && lineCurrencies.size === 1) {
    const [lineCurrency] = Array.from(lineCurrencies);
    if (lineCurrency !== orderCurrency) {
      const amount = items.reduce((sum, item) => sum + Number(
        item.totalCost ?? Number(item.purchaseQuantity ?? item.quantity ?? 0) * Number(item.unitCost ?? 0),
      ), 0);
      return { amount: Math.round(amount * 100) / 100, currency: lineCurrency };
    }
  }
  return { amount: Number(order.totalAmount || 0), currency: orderCurrency };
}
